import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  FiBookmark, FiCamera, FiImage, FiLoader, FiLock, FiLogOut, FiSave, FiUploadCloud,
} from 'react-icons/fi';
import { cChangePassword, cSavedPosts, cUpdateMe, cUpload, cUserPosts } from '../../api';
import { useCommunity } from '../../contexts/CommunityContext';
import { useLanguage } from '../../i18n';
import PostCard from '../../components/community/PostCard';
import ImageLightbox from '../../components/community/ImageLightbox';
import UserAvatar from '../../components/community/UserAvatar';
import UserBadges from '../../components/community/UserBadges';
import { NAVY, BLUE } from '../../components/community/brand';

/** My community profile: full information (editable) + my articles + ★ saved + password. */
export default function CommunityMePage() {
  const { t } = useLanguage();
  const { user, requireAuth, refresh, logout, setSession } = useCommunity();
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') || 'profile';
  const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:8000';
  const [lightbox, setLightbox] = useState(null);   // profile photo modal

  const [form, setForm] = useState(null);
  const [posts, setPosts] = useState([]);
  const [savedPosts, setSavedPosts] = useState([]);   // ★ favourites
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState('');
  const [pwd, setPwd] = useState({ old_password: '', new_password: '' });
  const avatarRef = useRef(null);
  const coverRef = useRef(null);

  useEffect(() => {
    if (!requireAuth(t('loginRequiredDesc'))) return;
    if (user && !form) {
      setForm({
        username: user.username || '',
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        gender: user.gender || '',
        bio: user.bio || '',
        website: user.website || '',
        address: user.address || '',
        city: user.city || '',
        country: user.country || '',
        avatar: user.avatar || '',
        cover: user.cover || '',
        social: {
          facebook: user.social?.facebook || '',
          telegram: user.social?.telegram || '',
          tiktok: user.social?.tiktok || '',
          youtube: user.social?.youtube || '',
          instagram: user.social?.instagram || '',
        },
      });
    }
  }, [user, form, requireAuth, t]);

  useEffect(() => {
    if (!user) return;
    cUserPosts(user.id, { limit: 20 })
      .then((res) => setPosts(res?.items || []))
      .catch(() => setPosts([]));
  }, [user]);

  // ★ Favourites are only fetched when the Saved tab is opened.
  useEffect(() => {
    if (!user || tab !== 'saved') return undefined;
    let alive = true;
    cSavedPosts({ limit: 20 })
      .then((res) => { if (alive) setSavedPosts(res?.items || []); })
      .catch(() => { if (alive) setSavedPosts([]); });
    return () => { alive = false; };
  }, [user, tab]);

  if (!user || !form) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-16 text-center">
        <p className="font-bold">{t('loginRequiredTitle')}</p>
        <p className="text-sm text-gray-500 mt-1">{t('loginRequiredDesc')}</p>
      </div>
    );
  }

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setSocial = (k) => (e) => setForm((f) => ({ ...f, social: { ...f.social, [k]: e.target.value } }));

  const upload = async (kind, file) => {
    if (!file) return;
    setUploading(kind);
    try {
      const res = await cUpload([file]);
      const url = res.urls?.[0] || '';
      setForm((f) => ({ ...f, [kind]: url }));
      toast.success(t('uploadImage'));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('somethingWrong'));
    } finally {
      setUploading('');
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const updated = await cUpdateMe(form);
      toast.success(t('saved'));
      refresh();
      // keep the cached session user in sync
      if (updated?.id) setSession({ access_token: localStorage.getItem('ms_community_token'), user: { ...user, ...updated } });
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('somethingWrong'));
    } finally {
      setBusy(false);
    }
  };

  const changePwd = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await cChangePassword(pwd);
      toast.success(t('saved'));
      setPwd({ old_password: '', new_password: '' });
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('somethingWrong'));
    } finally {
      setBusy(false);
    }
  };

  const input = 'w-full rounded-xl border border-gray-200 dark:border-gray-700 dark:bg-gray-900 px-3.5 py-2.5 text-sm focus:border-gray-400 outline-none';
  const label = 'block text-xs font-bold text-gray-500 mb-1';

  return (
    <div className="mx-auto max-w-3xl px-3 lg:px-4 py-4 space-y-4">
      <section className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 overflow-hidden">
        <div
          className="h-32 sm:h-44 bg-cover bg-center relative"
          style={form.cover
            ? { backgroundImage: `url(${form.cover.startsWith('http') ? form.cover : `${apiBase}${form.cover}`})` }
            : { background: 'linear-gradient(135deg,#0B5FDB,#083E93)' }}
        >
          <button
            type="button"
            onClick={() => coverRef.current && coverRef.current.click()}
            className="absolute bottom-2 right-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/45 text-white text-xs font-bold"
          >
            {uploading === 'cover' ? <FiLoader className="w-3.5 h-3.5 animate-spin" /> : <FiImage className="w-3.5 h-3.5" />}
            {t('cover')}
          </button>
          <input ref={coverRef} type="file" accept="image/*" hidden onChange={(e) => upload('cover', e.target.files?.[0])} />
        </div>

        <div className="px-4 pb-4">
          <div className="flex items-end gap-3 -mt-10">
            <div className="relative">
              <button
                type="button"
                data-sfx="pop"
                onClick={() => setLightbox(form.avatar
                  ? {
                    src: form.avatar.startsWith('http') ? form.avatar : `${apiBase}${form.avatar}`,
                    caption: `${form.name || form.username} · @${form.username}`,
                  }
                  : null)}
                className="rounded-full"
                title={t('viewPhoto')}
              >
                <UserAvatar user={{ ...user, avatar: form.avatar }} size={84} ring />
              </button>
              <button
                type="button"
                onClick={() => avatarRef.current && avatarRef.current.click()}
                className="absolute bottom-0 right-0 p-2 rounded-full text-white shadow"
                style={{ background: BLUE }}
                aria-label={t('avatar')}
              >
                {uploading === 'avatar' ? <FiLoader className="w-3.5 h-3.5 animate-spin" /> : <FiCamera className="w-3.5 h-3.5" />}
              </button>
              <input ref={avatarRef} type="file" accept="image/*" hidden onChange={(e) => upload('avatar', e.target.files?.[0])} />
            </div>
            <div className="flex-1 min-w-0 pb-1">
              <h1 className="font-extrabold text-lg truncate flex items-center gap-1.5">
                <span className="truncate">{form.name || form.username}</span>
                <UserBadges user={user} size="lg" />
              </h1>
              <p className="text-xs text-gray-500">@{form.username}</p>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-3 gap-2 text-center text-sm">
            <div className="rounded-xl bg-gray-50 dark:bg-gray-800 py-2">
              <b>{posts.length}</b>
              <p className="text-[11px] text-gray-500">{t('posts')}</p>
            </div>
            <div className="rounded-xl bg-gray-50 dark:bg-gray-800 py-2">
              <b>{user.followers_count || 0}</b>
              <p className="text-[11px] text-gray-500">{t('followers')}</p>
            </div>
            <div className="rounded-xl bg-gray-50 dark:bg-gray-800 py-2">
              <b>{user.following_count || 0}</b>
              <p className="text-[11px] text-gray-500">{t('following')}</p>
            </div>
          </div>

          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => setParams({})}
              className={`px-4 py-2 rounded-full text-xs font-bold ${tab === 'profile' ? 'text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300'}`}
              style={tab === 'profile' ? { background: NAVY } : undefined}
            >
              {t('editProfile')}
            </button>
            <button
              type="button"
              onClick={() => setParams({ tab: 'posts' })}
              className={`px-4 py-2 rounded-full text-xs font-bold ${tab === 'posts' ? 'text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300'}`}
              style={tab === 'posts' ? { background: NAVY } : undefined}
            >
              {t('myPosts')} ({posts.length})
            </button>
            <button
              type="button"
              onClick={() => setParams({ tab: 'saved' })}
              className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold ${tab === 'saved' ? 'text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300'}`}
              style={tab === 'saved' ? { background: NAVY } : undefined}
            >
              <FiBookmark className="w-3.5 h-3.5" /> {t('savedPosts')} ({savedPosts.length})
            </button>
            <button
              type="button"
              onClick={() => logout()}
              className="ml-auto inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-bold text-red-600 hover:bg-red-50 dark:hover:bg-gray-800"
            >
              <FiLogOut className="w-3.5 h-3.5" /> {t('signOut')}
            </button>
          </div>
        </div>
      </section>

      {tab === 'posts' ? (
        posts.length === 0 ? (
          <p className="text-sm text-gray-400 px-1">{t('noPosts')}</p>
        ) : (
          posts.map((p) => (
            <PostCard
              key={p.id}
              post={p}
              onChanged={(updated) => setPosts((prev) => prev.map((x) => (x.id === updated.id ? { ...x, ...updated } : x)))}
              onDeleted={(id) => setPosts((prev) => prev.filter((x) => x.id !== id))}
            />
          ))
        )
      ) : tab === 'saved' ? (
        // ★ Saved articles — un-saving a card removes it from this list.
        savedPosts.length === 0 ? (
          <p className="text-sm text-gray-400 px-1">{t('noSavedPosts')}</p>
        ) : (
          savedPosts.map((p) => (
            <PostCard
              key={p.id}
              post={{ ...p, saved: true }}
              onChanged={(updated) => setSavedPosts((prev) => (updated.saved === false
                ? prev.filter((x) => x.id !== updated.id)
                : prev.map((x) => (x.id === updated.id ? { ...x, ...updated } : x))))}
              onDeleted={(id) => setSavedPosts((prev) => prev.filter((x) => x.id !== id))}
            />
          ))
        )
      ) : (
        <form onSubmit={save} className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-4 space-y-4">
          <h2 className="font-extrabold text-sm">{t('editProfile')}</h2>

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className={label}>{t('username')}</label>
              <input value={form.username} onChange={set('username')} className={input} />
            </div>
            <div>
              <label className={label}>{t('fullName')}</label>
              <input value={form.name} onChange={set('name')} className={input} />
            </div>
            <div>
              <label className={label}>{t('emailOpt')}</label>
              <input type="email" value={form.email} onChange={set('email')} className={input} />
            </div>
            <div>
              <label className={label}>{t('phone')}</label>
              <input value={form.phone} onChange={set('phone')} className={input} />
            </div>
            <div>
              <label className={label}>{t('gender')}</label>
              <select value={form.gender} onChange={set('gender')} className={input}>
                <option value="">—</option>
                <option value="male">{t('male')}</option>
                <option value="female">{t('female')}</option>
                <option value="other">{t('other')}</option>
              </select>
            </div>
            <div>
              <label className={label}>{t('website')}</label>
              <input value={form.website} onChange={set('website')} placeholder="https://" className={input} />
            </div>
            <div>
              <label className={label}>{t('city')}</label>
              <input value={form.city} onChange={set('city')} className={input} />
            </div>
            <div>
              <label className={label}>{t('country')}</label>
              <input value={form.country} onChange={set('country')} className={input} />
            </div>
          </div>

          <div>
            <label className={label}>{t('address')}</label>
            <input value={form.address} onChange={set('address')} className={input} />
          </div>

          <div>
            <label className={label}>{t('bio')}</label>
            <textarea value={form.bio} onChange={set('bio')} rows={3} className={input} />
          </div>

          <div>
            <label className={label}>{t('socialLinks')}</label>
            <div className="grid gap-3 sm:grid-cols-2">
              <input value={form.social.facebook} onChange={setSocial('facebook')} placeholder="Facebook URL" className={input} />
              <input value={form.social.telegram} onChange={setSocial('telegram')} placeholder="Telegram URL" className={input} />
              <input value={form.social.tiktok} onChange={setSocial('tiktok')} placeholder="TikTok URL" className={input} />
              <input value={form.social.youtube} onChange={setSocial('youtube')} placeholder="YouTube URL" className={input} />
            </div>
          </div>

          <button
            type="submit"
            disabled={busy}
            className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl text-white font-bold disabled:opacity-60"
            style={{ background: NAVY }}
          >
            {busy ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiSave className="w-4 h-4" />} {t('save')}
          </button>
        </form>
      )}

      {tab === 'profile' && (
        <form onSubmit={changePwd} className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-4 space-y-3">
          <h2 className="font-extrabold text-sm inline-flex items-center gap-2">
            <FiLock className="w-4 h-4" style={{ color: BLUE }} /> {t('changePassword')}
          </h2>
          <div className="grid gap-3 sm:grid-cols-2">
            <input
              type="password"
              value={pwd.old_password}
              onChange={(e) => setPwd((p) => ({ ...p, old_password: e.target.value }))}
              placeholder={t('currentPassword')}
              className={input}
            />
            <input
              type="password"
              value={pwd.new_password}
              onChange={(e) => setPwd((p) => ({ ...p, new_password: e.target.value }))}
              placeholder={t('newPassword')}
              className={input}
            />
          </div>
          <button
            type="submit"
            disabled={busy || !pwd.old_password || !pwd.new_password}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-white text-sm font-bold disabled:opacity-60"
            style={{ background: BLUE }}
          >
            <FiUploadCloud className="w-4 h-4" /> {t('changePassword')}
          </button>
        </form>
      )}

      {tab === 'profile' && (
        <p className="text-center text-xs text-gray-400">
          <Link to={`/u/${user.username}`} className="underline">{t('profileNav')} @{user.username}</Link>
        </p>
      )}

      {/* profile photo modal */}
      {lightbox && lightbox.src && (
        <ImageLightbox
          src={lightbox.src}
          alt={lightbox.caption || 'profile'}
          caption={lightbox.caption}
          onClose={() => setLightbox(null)}
        />
      )}
    </div>
  );
}
